"use client";

import { Button } from "@/03-presenters/components/ui/button";
import { Input } from "@/03-presenters/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/03-presenters/components/ui/select";
import { locales } from "@/i18n";
import { cn } from "@/lib/utils";
import { cx } from "class-variance-authority";
import { useLocale, useTranslations } from "next-intl";
import { parseAsInteger, useQueryState } from "nuqs";
import { useEffect, useMemo, useState } from "react";
import { useTableStore } from "../TableStoreProvider";
import { countryStore } from "@/03-presenters/components/MultiCountrySelect";
import { getCurrencySymbol } from "@/03-presenters/shared/helper";
type SelectOption = {
  label?: string;
  displayName?: string;
};
export const JOINT_LABEL = "__joint__";
export type SelectCompProps = {
  options: SelectOption[];
  filterKey: string;
  filterDisplayName: string;
  useV2?: boolean;
};
export function getStartAndEndValue(option: string | null) {
  if (option?.includes(JOINT_LABEL)) {
    const [startValue, endValue] = option.split(JOINT_LABEL);
    return [startValue, endValue];
  }
  return [undefined, undefined];
}
export function shouldConvertDuration(filterKey: string) {
  return ["tiktok_video_search_duration"].includes(filterKey);
}
export function shouldShowPercent(filterKey: string) {
  return ["tiktok_ads_search_like_rate", "tiktok_product_search_commission_rate", "tiktok_creator_search_engagement_rate", "tiktok_video_search_engagement_rate", "tiktok_video_search_engagement_rate", "tiktok_ads_search_engagement_rate", "tiktok_product_search_creator_sold_rate", "shopify_shop_search_traffic_sources_direct", "shopify_shop_search_traffic_sources_mail", "shopify_shop_search_traffic_sources_referrals", "shopify_shop_search_traffic_sources_social", "shopify_shop_search_traffic_sources_search", "shopify_shop_search_traffic_sources_paid_referrals", "shopify_product_search_traffic_sources_direct", "shopify_product_search_traffic_sources_mail", "shopify_product_search_traffic_sources_referrals", "shopify_product_search_traffic_sources_social", "shopify_product_search_traffic_sources_search", "shopify_product_search_traffic_sources_paid_referrals"].includes(filterKey);
}
export default function CustomSelectCompV2({
  filterKey,
  options: _options,
  filterDisplayName
}: SelectCompProps) {
  const t = useTranslations("EntityView.filter");
  const tableStore = useTableStore();
  const updateFilter = tableStore(state => state.updateFilter);
  const filterValue = tableStore(state => state.filters.find(filter => filter.filterKey === filterKey)?.value);
  const countryCode = countryStore(state => state.countryCode);
  const currencySymbol = getCurrencySymbol(countryCode);
  const shouldReplaceCurrencySymbol = countryCode !== "US";
  const options = [{
    label: "_ALL_",
    displayName: t("all")
  }, ..._options];
  const setPageIndex = tableStore(state => state.setPageIndex);
  const [startValue, setStartValue] = useState<string>();
  const [endValue, setEndValue] = useState<string>();
  const [open, setOpen] = useState(false);
  const defaultValue = filterValue || "_ALL_";
  const handleValueChange = (value: string) => {
    setStartValue("");
    setEndValue("");
    setOpen(false);
    setPageIndex(0);
    updateFilter(filterKey, value);
  };
  useEffect(() => {
    setStartValue(getStartAndEndValue(defaultValue)[0]);
    setEndValue(getStartAndEndValue(defaultValue)[1]);
  }, [defaultValue]);
  const showPercent = shouldShowPercent(filterKey);
  function handleClick() {
    if (!startValue && !endValue) {
      return;
    }
    if (startValue && endValue && Number(startValue) >= Number(endValue)) {
      return;
    }
    const option = `${startValue ?? ""}${JOINT_LABEL}${endValue ?? ""}`;
    setOpen(false);
    setPageIndex(0);
    updateFilter(filterKey, option);
  }
  const isInvalid = useMemo(() => startValue && endValue && Number(startValue) >= Number(endValue), [startValue, endValue]);
  const label = (() => {
    if (defaultValue?.includes(JOINT_LABEL)) {
      const [startValue, endValue] = defaultValue.split(JOINT_LABEL);
      if (startValue && endValue) {
        return `${filterDisplayName}: ${startValue}${showPercent ? "%" : ""}-${endValue}${showPercent ? "%" : ""}`;
      }
      if (startValue) {
        return `${filterDisplayName} > ${startValue}${showPercent ? "%" : ""}`;
      }
      return `${filterDisplayName} < ${endValue}${showPercent ? "%" : ""}`;
    }
    return defaultValue && defaultValue !== "_ALL_" ? `${filterDisplayName}: ${options.find(option => option.label === defaultValue)?.displayName}` : filterDisplayName;
  })();
  return <Select value={defaultValue} onValueChange={handleValueChange} open={open} data-sentry-element="Select" data-sentry-component="CustomSelectCompV2" data-sentry-source-file="CustomSelectCompV2.tsx">
      <SelectTrigger className={cx("m-1 h-7 py-0 text-default-black")} onClick={() => setOpen(true)} data-sentry-element="SelectTrigger" data-sentry-source-file="CustomSelectCompV2.tsx">
        <SelectValue placeholder={filterDisplayName} data-sentry-element="SelectValue" data-sentry-source-file="CustomSelectCompV2.tsx">
          {label?.replaceAll("$", currencySymbol)}
        </SelectValue>
      </SelectTrigger>
      <SelectContent onKeyDown={e => {
      e.stopPropagation();
    }} onPointerDownOutside={() => setOpen(false)} data-sentry-element="SelectContent" data-sentry-source-file="CustomSelectCompV2.tsx">
        <SelectGroup onKeyDown={() => {}} data-sentry-element="SelectGroup" data-sentry-source-file="CustomSelectCompV2.tsx">
          {options.map(option => {
          return <SelectItem key={option.label} value={option.label ?? ""}>
                {/* do not delete!!! important !!!! */}
                <span className="hidden">******</span>
                {shouldReplaceCurrencySymbol ? option.displayName?.replaceAll("$", currencySymbol) : option.displayName}
              </SelectItem>;
        })}
        </SelectGroup>
        <div className="mt-2 flex items-center justify-between border-t-1 border-solid border-gray-3 pt-2">
          <Input className={cn("h-6 w-24 focus-visible:ring-0 focus-visible:ring-offset-0", isInvalid && "border-red-500")} type="number" value={startValue} onChange={e => {
          setStartValue(e.target.value);
        }} data-sentry-element="Input" data-sentry-source-file="CustomSelectCompV2.tsx"></Input>
          {showPercent && "%"}
          <span className="mx-2">-</span>
          <Input className={cn("h-6 w-24 focus-visible:ring-0 focus-visible:ring-offset-0", isInvalid && "border-red-500")} type="number" value={endValue} onChange={e => {
          setEndValue(e.target.value);
        }} data-sentry-element="Input" data-sentry-source-file="CustomSelectCompV2.tsx"></Input>
          {showPercent && "%"}
        </div>
        <div className="mt-2 flex justify-between">
          <Button variant="link" className="h-6 hover:no-underline" onClick={() => {
          setStartValue("");
          setEndValue("");
          updateFilter(filterKey, "");
        }} data-sentry-element="Button" data-sentry-source-file="CustomSelectCompV2.tsx">
            重置
          </Button>
          <Button className="h-6" onClick={handleClick} data-sentry-element="Button" data-sentry-source-file="CustomSelectCompV2.tsx">
            确认
          </Button>
        </div>
      </SelectContent>
    </Select>;
}